import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Alert,
  Snackbar,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
} from '@mui/icons-material';
import * as MuiIcons from '@mui/icons-material';
import placesService from '../services/placesService';
import { useThemeCustom } from '../hooks/useThemeCustom';
import { useContextualHint } from '../hooks/useContextualHint';
import placeServicesService from '../services/placeServicesService';
import placeProfessionalsService from '../services/placeProfessionalsService';
import usePlacesCRUD from '../hooks/usePlacesCRUD';
import { PlacesDataProvider } from '../contexts/PlacesDataContext';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';
import LoadingState from '../components/ui/LoadingState';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import ContextualHint from '../components/ContextualHint';
import DeleteConfirmDialog from '../components/ui/DeleteConfirmDialog';
import PlaceForm from '../components/forms/PlaceForm';
import PlaceCardNormalized from '../components/places/PlaceCardNormalized';
import PlaceServicePaymentDialog from '../components/places/PlaceServicePaymentDialog';
import { handleDeleteOperation } from '../utils/apiErrorHandler';
import PlaceServicesManager from '../components/places/PlaceServicesManager';
import PlaceProfessionalsManager from '../components/places/PlaceProfessionalsManager';
import ProfessionalServicesDialog from '../components/places/ProfessionalServicesDialog';
import PlaceCreationWizard from '../components/places/PlaceCreationWizard';

// Helper para obter ícone dinamicamente
const getIconComponent = (iconName) => {
  const IconComponent = MuiIcons[iconName];
  return IconComponent || MuiIcons.HelpOutline;
};

/**
 * PlacesPage - Versão V3 com estrutura normalizada
 * 
 * Melhorias V3:
 * - API retorna estrutura normalizada { professionals, services, payments, places }
 * - Cache local de dados auxiliares (professionals, services, payments)
 * - Lookup O(1) via Maps ao invés de dados duplicados
 * - Redução de 70-90% no payload da API
 * - Dados auxiliares disponíveis durante toda a sessão da página
 */
export default function PlacesPage() {
  // ✅ Hook especializado para Places normalizados
  const { data, places, loading, error, reload, create, update, delete: deleteItem } = usePlacesCRUD(placesService);
  
  // 🔍 TESTE: Importar com safe fallback e logs
  let getIcon, getLabel;
  try {
    const themeContext = useThemeCustom();
    getIcon = themeContext.getIcon;
    getLabel = themeContext.getLabel;
    console.log('[PlacesPage] ✅ getIcon e getLabel obtidos do hook com sucesso');
  } catch (error) {
    console.warn('[PlacesPage] ⚠️ Erro ao obter getIcon/getLabel do hook:', error.message);
    getIcon = (key) => ({ name: 'HelpOutline' });
    getLabel = (key) => {
      const fallbacks = { 
        places: 'Locais', 
        placesDescription: 'Gerencie seus locais' 
      };
      console.debug(`[PlacesPage.getLabel] Fallback para "${key}"`);
      return fallbacks[key] || key;
    };
  }
  
  const formDialog = useDialog();
  const creationWizardDialog = useDialog(); // Wizard para criação de novo local
  const deleteDialog = useDialog();
  const deleteServiceDialog = useDialog();
  const deleteProfessionalDialog = useDialog();
  const paymentDialog = useDialog();
  const servicesDialog = useDialog();
  const professionalsDialog = useDialog();
  const professionalServicesDialog = useDialog();
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  // Obter dica contextual
  const hint = useContextualHint('places', { places, data });

  // ✅ Handlers
  const handleCreate = async (data) => {
    try {
      await create(data);
      formDialog.handleClose();
      showSuccess('Local criado com sucesso!');
    } catch (err) {
      showError('Erro ao criar local: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdate = async (data) => {
    try {
      await update(formDialog.data.id, data);
      formDialog.handleClose();
      showSuccess('Local atualizado com sucesso!');
    } catch (err) {
      showError('Erro ao atualizar local: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmit = formDialog.isEdit ? handleUpdate : handleCreate;

  const handleDeleteFromForm = async (place) => {
    // Usar mesmo dialog de confirmação
    formDialog.handleClose();
    deleteDialog.handleOpen(place);
  };

  const handleDelete = async () => {
    try {
      await deleteItem(deleteDialog.data.id);
      deleteDialog.handleClose();
      showSuccess('Local excluído com sucesso!');
    } catch (err) {
      showError('Erro ao excluir local: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleRemoveService = async (place, placeService) => {
    // Abrir dialog de confirmação ao invés de window.confirm
    deleteServiceDialog.handleOpen({ place, placeService });
  };

  const handleConfirmRemoveService = async () => {
    const { place, placeService } = deleteServiceDialog.data;
    
    try {
      // ✅ Usar handleDeleteOperation para tratar respostas corretamente
      const result = await handleDeleteOperation(
        (params) => placeServicesService.delete(params.placeId, params.serviceId),
        { placeId: place.id, serviceId: placeService.serviceId }
      );

      if (result.success) {
        // Sucesso (HTTP 204)
        await reload();
        deleteServiceDialog.handleClose();
        showSuccess('Serviço removido do local com sucesso!');
      } else {
        // Erro (400, 404, 500, etc)
        const errorMsg = result.error.message;
        showError(errorMsg);
      }
    } catch (err) {
      // Fallback para erros inesperados
      showError('Erro ao remover serviço: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleRemoveProfessional = async (place, placeProfessional) => {
    // Abrir dialog de confirmação ao invés de window.confirm
    deleteProfessionalDialog.handleOpen({ place, placeProfessional });
  };

  const handleConfirmRemoveProfessional = async () => {
    const { place, placeProfessional } = deleteProfessionalDialog.data;
    
    try {
      // ✅ Usar handleDeleteOperation para tratar respostas corretamente
      const result = await handleDeleteOperation(
        (params) => placeProfessionalsService.delete(params.placeId, params.id),
        { placeId: place.id, id: placeProfessional.id }
      );

      if (result.success) {
        // Sucesso (HTTP 204)
        await reload();
        deleteProfessionalDialog.handleClose();
        showSuccess('Profissional removido do local com sucesso!');
      } else {
        // Erro (400, 404, 500, etc)
        const errorMsg = result.error.message;
        showError(errorMsg);
      }
    } catch (err) {
      // Fallback para erros inesperados
      showError('Erro ao remover profissional: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleConfigureProfessionalServices = (place, placeProfessional) => {
    professionalServicesDialog.handleOpen({ place, placeProfessional });
  };

  // ✅ Loading/Error States
  if (loading) return <LoadingState message="Carregando locais..." />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <PlacesDataProvider data={data}>
      <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {React.createElement(getIconComponent(getIcon('places').name), { sx: { mr: 1, verticalAlign: 'middle', fontSize: 32 } })}
            {getLabel('places')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getLabel('placesDescription')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => creationWizardDialog.handleOpen()}
        >
          Novo Local
        </Button>
      </Box>

      {/* Dica Contextual */}
      <ContextualHint hint={hint} onActionClick={() => creationWizardDialog.handleOpen()} />

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Gerenciamento visual:</strong> Todos os serviços e profissionais são exibidos nos cards. 
          Clique no ícone de configuração ao lado de cada serviço para gerenciar formas de pagamento aceitas.
        </Typography>
      </Alert>

      {/* Empty State */}
      {places.length === 0 ? (
        <EmptyState
          icon={getIconComponent(getIcon('places').name)}
          title="Nenhum local cadastrado"
          message="Comece criando seu primeiro local para oferecer serviços"
          action={
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => creationWizardDialog.handleOpen()}>
              Criar Primeiro Local
            </Button>
          }
        />
      ) : (
        <Grid container spacing={3}>
          {places.map((place) => (
            <Grid item xs={12} md={6} lg={6} key={place.id}>
              <PlaceCardNormalized
                place={place}
                onEdit={(place) => formDialog.handleOpen(place)}
                onDelete={(place) => deleteDialog.handleOpen(place)}
                onConfigurePayments={(serviceLink) => {
                  // Adicionar placeId ao serviceLink antes de passar para o dialog
                  paymentDialog.handleOpen({ ...serviceLink, placeId: place.id });
                }}
                onManageServices={(place) => servicesDialog.handleOpen(place)}
                onManageProfessionals={(place) => professionalsDialog.handleOpen(place)}
                onRemoveService={handleRemoveService}
                onRemoveProfessional={handleRemoveProfessional}
                onConfigureProfessionalServices={handleConfigureProfessionalServices}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Place Creation Wizard - Para criação de novos locais */}
      <PlaceCreationWizard
        open={creationWizardDialog.open}
        onClose={creationWizardDialog.handleClose}
        onSuccess={() => {
          creationWizardDialog.handleClose();
          reload();
          showSuccess('Local criado com sucesso!');
        }}
        existingProfessionals={data?.professionals || []}
        existingServices={data?.services || []}
      />

      {/* Place Form Dialog - Apenas para edição */}
      <PlaceForm
        open={formDialog.open}
        onClose={formDialog.handleClose}
        onSubmit={handleSubmit}
        onDelete={handleDeleteFromForm}
        data={formDialog.data}
      />

      {/* Payment Configuration Dialog */}
      <PlaceServicePaymentDialog
        open={paymentDialog.open}
        onClose={paymentDialog.handleClose}
        placeService={paymentDialog.data}
        onUpdate={reload}
      />

      {/* Delete Place Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onConfirm={handleDelete}
        title="Confirmar Exclusão de Local"
        itemName={deleteDialog.data?.name}
        itemType="local"
        severity="error"
        impacts={[
          'Todos os serviços vinculados serão removidos',
          'Todos os profissionais perderão o vínculo com este local',
          'Configurações de pagamento serão perdidas',
          'Agendamentos vinculados poderão ser afetados'
        ]}
      />

      {/* Delete Service from Place Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteServiceDialog.open}
        onClose={deleteServiceDialog.handleClose}
        onConfirm={handleConfirmRemoveService}
        title="Remover Serviço do Local"
        itemName={deleteServiceDialog.data?.placeService?.serviceName || deleteServiceDialog.data?.placeService?.service?.name}
        itemType="serviço"
        message={`Tem certeza que deseja remover este serviço do local "${deleteServiceDialog.data?.place?.name}"?`}
        severity="warning"
        impacts={[
          'Configurações de pagamento deste serviço no local serão perdidas',
          'Agendamentos futuros vinculados a este serviço no local podem ser afetados'
        ]}
      />

      {/* Delete Professional from Place Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteProfessionalDialog.open}
        onClose={deleteProfessionalDialog.handleClose}
        onConfirm={handleConfirmRemoveProfessional}
        title="Remover Profissional do Local"
        itemName={deleteProfessionalDialog.data?.placeProfessional?.professionalName || deleteProfessionalDialog.data?.placeProfessional?.professional?.name}
        itemType="profissional"
        message={`Tem certeza que deseja remover este profissional do local "${deleteProfessionalDialog.data?.place?.name}"?`}
        severity="warning"
        impacts={[
          'O profissional não poderá mais atender neste local',
          'Agendamentos futuros deste profissional no local podem ser afetados',
          'Será necessário vincular novamente se precisar restaurar'
        ]}
      />

      {/* Place Services Manager */}
      <PlaceServicesManager
        open={servicesDialog.open}
        onClose={servicesDialog.handleClose}
        place={servicesDialog.data}
        onUpdate={reload}
      />

      {/* Place Professionals Manager */}
      <PlaceProfessionalsManager
        open={professionalsDialog.open}
        onClose={professionalsDialog.handleClose}
        place={professionalsDialog.data}
        onUpdate={reload}
      />

      {/* Professional Services Dialog */}
      <ProfessionalServicesDialog
        open={professionalServicesDialog.open}
        onClose={professionalServicesDialog.handleClose}
        placeProfessional={professionalServicesDialog.data?.placeProfessional}
        place={professionalServicesDialog.data?.place}
        onUpdate={reload}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </PlacesDataProvider>
  );
}
